/**
 * Internal notes & per-trip audit contracts — cross-cutting back-office surface (TW-3457).
 *
 * **Internal notes** are TC-only annotations on a trip: amber, author + timestamp, and *never*
 * visible to a club user. That guarantee is enforced in the database by the `internal_note`
 * back-office-only RLS policy (data-model §10.3) — the app's `@UserType("BACK_OFFICE")` gate is the
 * first line, RLS is the backstop. The Zod schema here is the single source of truth for the post
 * body (API + web).
 *
 * **Audit** is the read side of the append-only audit log (data-model §8), assembled *per trip* from
 * the rows the domain modules write explicitly in-transaction: trip status/owner changes, proposal
 * sends/acceptance/deposit, manifest assignments, roster (passenger) edits, invoice transitions and
 * manifest exports. The presentation fold (`describeAuditEntry`) is pure so the web and any test
 * agree on the icon + label for a row without a runtime; the web localizes the returned key (the web
 * has no test runner, so this is proven in the API vitest harness — mirrors the other shared folds).
 */
import { z } from "zod";
import { type TripStatus } from "./constants";

// ── Internal notes ────────────────────────────────────────────────────────────

/** A TC-internal note on a trip. Author resolved server-side; never serialised to a club user. */
export interface InternalNoteDto {
  id: string;
  tripId: string;
  authorId: string | null;
  authorName: string | null;
  body: string;
  createdAt: string;
}

/** The single source of truth for the note compose body (API `ZodValidationPipe` + web form). */
export const createInternalNoteSchema = z.object({
  body: z.string().trim().min(1).max(5000),
});
export type CreateInternalNoteInput = z.infer<typeof createInternalNoteSchema>;

// ── Audit ───────────────────────────────────────────────────────────────────

/**
 * One audit-log row as surfaced in the per-trip audit view. `before`/`after` are the (sanitised)
 * snapshots the writers recorded; `actorName` is resolved from the actor id (null for system/seed
 * rows). `entityType`/`action` mirror the audit_log columns — `describeAuditEntry` folds them to a
 * presentation descriptor.
 */
export interface AuditEntryDto {
  id: string;
  entityType: string;
  action: string;
  actorName: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string;
}

/** Semantic category of an audit entry — drives the timeline grouping/styling. */
export type AuditKind =
  | "tripStatus"
  | "tripOwner"
  | "proposal"
  | "deposit"
  | "manifest"
  | "roster"
  | "invoice"
  | "export"
  | "other";

/**
 * Icon name for an audit entry. Each value is a valid web `IconName`; the web casts it directly.
 * Kept as a string-literal union so a typo is caught here rather than rendering the fallback icon.
 */
export type AuditIcon =
  | "trips"
  | "user"
  | "send"
  | "doc"
  | "checkCircle"
  | "roster"
  | "invoices"
  | "download"
  | "clock"
  | "x";

/** Presentation descriptor for an audit row: which icon, which i18n label, and its interpolations. */
export interface AuditDescriptor {
  kind: AuditKind;
  icon: AuditIcon;
  /** i18n key under `audit.*`. */
  labelKey: string;
  /** Interpolation values for `labelKey`. `status` (trip status) is localized by the web first. */
  labelParams: Record<string, string>;
}

/** Read a string field off a sanitised snapshot, tolerating missing/non-string values. */
function str(obj: Record<string, unknown> | null, key: string): string | undefined {
  const v = obj?.[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/**
 * A passenger's display name from an audit snapshot: `firstName lastName` for current rows,
 * falling back to the legacy single `fullName` field for audit entries written before the split.
 */
function passengerName(obj: Record<string, unknown> | null): string | undefined {
  const joined = [str(obj, "firstName"), str(obj, "lastName")].filter(Boolean).join(" ");
  return joined.length > 0 ? joined : str(obj, "fullName");
}

/**
 * Fold an audit row to its timeline presentation (pure). Maps each `(entityType, action)` the domain
 * modules write to an icon + label key + params. Status-change rows carry their new status in
 * `after.status`; roster create/update rows carry the passenger `fullName` (the snapshot deliberately
 * omits PII — see the passengers repository). Anything unrecognised falls back to a neutral entry that
 * echoes the raw action, so a future audit action never renders blank.
 */
export function describeAuditEntry(entry: AuditEntryDto): AuditDescriptor {
  const { entityType, action, after, before } = entry;
  const status = str(after, "status");
  const name = passengerName(after) ?? passengerName(before);

  switch (entityType) {
    case "trip":
      if (action === "owner_change") {
        const assigned = str(after, "ownerUserId") !== undefined;
        return {
          kind: "tripOwner",
          icon: "user",
          labelKey: assigned ? "audit.tripOwnerAssigned" : "audit.tripOwnerCleared",
          labelParams: {},
        };
      }
      return {
        kind: "tripStatus",
        icon: "trips",
        labelKey: "audit.tripStatus",
        labelParams: { status: status ?? "" },
      };

    case "proposal":
      if (action === "deposit_confirmed") {
        return { kind: "deposit", icon: "checkCircle", labelKey: "audit.depositConfirmed", labelParams: {} };
      }
      // A decision back office recorded for the club (TRAV-74): `reason` is an OnBehalfReason key the
      // web localizes into the "confirmed by phone/email/…" phrase.
      if (action === "status_change" && after?.onBehalfOfClub === true) {
        const reason = str(after, "reason") ?? "other";
        const labelKey =
          after.restored === true
            ? "audit.proposalRestoredOnBehalf"
            : after.rejected === true
              ? "audit.proposalRejectedOnBehalf"
              : "audit.proposalAcceptedOnBehalf";
        return { kind: "proposal", icon: after.rejected === true ? "x" : "checkCircle", labelKey, labelParams: { reason } };
      }
      if (status === "Sent") {
        return { kind: "proposal", icon: "send", labelKey: "audit.proposalSent", labelParams: {} };
      }
      if (status === "Accepted") {
        return { kind: "proposal", icon: "checkCircle", labelKey: "audit.proposalAccepted", labelParams: {} };
      }
      if (status === "Expired") {
        return { kind: "proposal", icon: "doc", labelKey: "audit.proposalExpired", labelParams: {} };
      }
      return { kind: "proposal", icon: "doc", labelKey: "audit.proposalStatus", labelParams: {} };

    case "trip_passenger": {
      // Snapshots carry the passenger's display name (name only — no doc PII) since TRAV-2, making
      // each add/remove row self-describing; older rows have no name → generic label fallback.
      const assignmentName = str(after, "passengerName") ?? str(before, "passengerName");
      const removed = action === "delete";
      return {
        kind: "manifest",
        icon: "roster",
        labelKey: removed
          ? assignmentName
            ? "audit.manifestRemoveNamed"
            : "audit.manifestRemove"
          : assignmentName
            ? "audit.manifestAddNamed"
            : "audit.manifestAdd",
        labelParams: assignmentName ? { name: assignmentName } : {},
      };
    }

    case "passenger":
      if (action === "delete") {
        return { kind: "roster", icon: "roster", labelKey: "audit.rosterRemove", labelParams: {} };
      }
      return {
        kind: "roster",
        icon: "roster",
        labelKey: action === "update" ? "audit.rosterUpdate" : "audit.rosterAdd",
        labelParams: name ? { name } : {},
      };

    case "invoice":
      if (status === "Issued") {
        return { kind: "invoice", icon: "invoices", labelKey: "audit.invoiceIssued", labelParams: {} };
      }
      if (status === "Paid") {
        return { kind: "invoice", icon: "invoices", labelKey: "audit.invoicePaid", labelParams: {} };
      }
      return { kind: "invoice", icon: "invoices", labelKey: "audit.invoiceStatus", labelParams: {} };

    case "document":
      return { kind: "export", icon: "download", labelKey: "audit.export", labelParams: {} };

    default:
      return { kind: "other", icon: "clock", labelKey: "audit.generic", labelParams: { action } };
  }
}

/** The trip statuses an audit `tripStatus` entry can carry (for exhaustive web localization). */
export type AuditTripStatus = TripStatus;
